import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Plane, Radio } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { STAGE_COLORS, isLive, stageLabel } from "@/lib/lifecycle";
import { StageBadge } from "@/components/StageBadge";
import { ArrivalsBoard } from "@/components/ArrivalsBoard";
import { RouteMap } from "@/components/RouteMap";

const lateness = (minutes) => {
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} min`;
};

const LEGEND = [
  ["driver_accepted", "Accepted"],
  ["en_route_pickup", "En route"],
  ["arrived_pickup", "At pickup"],
  ["onboard", "Onboard"],
  ["arrived_destination", "At destination"],
];

export default function DispatchLive() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(
    () => api.get("/dispatch/live").then(({ data }) => setData(data)).catch((e) => setError(errMsg(e))),
    []
  );

  useEffect(() => {
    load();
    const timer = setInterval(load, 12000);
    return () => clearInterval(timer);
  }, [load]);

  const trips = data?.trips || [];
  const live = trips.filter((t) => isLive(t.stage));
  const late = trips.filter((t) => t.late?.watching && (t.late.overdue || t.late.due_soon));
  const withGps = live.filter((t) => t.driver_location?.lat != null);
  const focus = selected ? trips.find((t) => t.id === selected) : null;
  const mapTrip = focus?.driver_location?.lat != null ? focus : withGps[0];
  // Drawn with the Maps JavaScript API, not the Maps Embed API — the Embed API is a separate
  // product that renders Google's own error page inside the iframe when it is not enabled.
  const mapPoints = mapTrip
    ? [{ lat: mapTrip.driver_location.lat, lng: mapTrip.driver_location.lng },
       ...(() => {
         const target = mapTrip.heading_to === "dropoff" ? mapTrip.dropoff : mapTrip.pickup;
         return target?.lat != null ? [{ lat: target.lat, lng: target.lng }] : [];
       })()]
    : [];

  return (
    <div data-testid="dispatch-live-page" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Operations</p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Live dispatch</h1>
          <p className="mt-2 text-sm text-slate-400">
            Every portal reads the same trip status. This board refreshes on its own every 12 seconds.
          </p>
        </div>
        <Link to="/app/dispatch" data-testid="dispatch-board-link" className="font-mono text-xs text-gold">
          Dispatch board →
        </Link>
      </div>

      {error && <p data-testid="dispatch-live-error" className="text-sm text-red-400">{error}</p>}

      {late.length > 0 && (
        <div data-testid="late-pickup-banner" className="space-y-2 rounded-xl border border-amber-500/40 bg-amber-500/5 p-5">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber-300">
            Needs a chauffeur moving · {late.length}
          </p>
          {late.map((t) => (
            <p key={t.id} data-testid={`late-pickup-${t.number}`} className="text-sm text-slate-300">
              <Link to={`/app/manifests/${t.id}`} className="font-mono text-xs text-gold hover:underline">#{t.number}</Link>{" "}
              {t.late.overdue
                ? `overdue by ${lateness(Math.abs(t.late.minutes_out))}`
                : `picks up in ${Math.round(t.late.minutes_out)} min`}
              {" · "}{t.chauffeur_name || "unassigned"}{" · "}{stageLabel(t.stage)}
            </p>
          ))}
        </div>
      )}

      <ArrivalsBoard />

      <div className="flex flex-wrap gap-4">
        {LEGEND.map(([stage, label]) => (          <span key={stage} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-400">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STAGE_COLORS[stage] }} /> {label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.15fr_1fr]">
        <div className="space-y-3">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gold">
            Active trips{data ? ` · ${live.length}` : ""}
          </p>
          {!data && (
            <div data-testid="dispatch-live-loading" className="space-y-3">
              {[0, 1].map((i) => (
                <div key={i} className="h-28 animate-pulse rounded-xl border border-ink-600 bg-ink-800" />
              ))}
            </div>
          )}
          {data && live.length === 0 && (
            <p data-testid="dispatch-live-empty" className="rounded-xl border border-ink-600 bg-ink-800 p-8 text-center text-sm text-slate-500">
              Nothing on the road right now.
            </p>
          )}
          {live.map((t) => (
            <button key={t.id} data-testid={`live-trip-${t.number}`} onClick={() => setSelected(t.id)}
              className={`w-full rounded-xl border p-4 text-left transition-colors ${
                selected === t.id ? "border-gold/50 bg-gold/5" : "border-ink-600 bg-ink-800 hover:border-gold/30"
              }`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-mono text-xs text-gold">#{t.number}</p>
                <div className="flex items-center gap-2">
                  {t.wait?.waiting && (
                    <span data-testid={`live-wait-${t.number}`}
                      className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${
                        t.wait.billing ? "border-amber-400/50 bg-amber-400/10 text-amber-300"
                          : "border-ink-600 text-slate-400"}`}>
                      {t.wait.billing ? `billing · ${Math.round(t.wait.billable_minutes)} min` : `waiting ${Math.round(t.wait.minutes)} min`}
                    </span>
                  )}
                  <StageBadge stage={t.stage} testId={`live-stage-${t.number}`} />
                </div>
              </div>
              <p className="mt-2 text-sm text-slate-100">{t.customer_name}</p>
              <p className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500">
                <CalendarClock className="h-3 w-3" /> {t.trip_date} {t.pickup_time}
              </p>
              <p className="mt-1.5 text-[11px] text-slate-400">
                {t.chauffeur_name || "Unassigned"}{t.vehicle_name ? ` · ${t.vehicle_name}` : ""}
                {t.driver_location ? "" : " · no GPS ping"}
              </p>
              {t.flight_number && (
                <p className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-400">
                  <Plane className="h-3 w-3 text-gold" /> {t.flight_number}
                  {t.flight_delay_minutes ? ` · ${t.flight_delay_minutes} min late` : ""}
                </p>
              )}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {mapPoints.length === 2 ? (
            <RouteMap points={mapPoints} testId="dispatch-map" vehicleAtStart />
          ) : (
            <div data-testid="dispatch-map-empty" className="flex h-80 items-center justify-center rounded-xl border border-ink-600 bg-ink-800 p-6 text-center text-sm text-slate-500">
              <span><Radio className="mx-auto mb-3 h-5 w-5 text-slate-600" />
                A driver's position appears here once they share GPS from the driver app.</span>
            </div>
          )}
          {focus && (
            <div data-testid="dispatch-focus" className="rounded-xl border border-ink-600 bg-ink-800 p-5">
              <p className="text-base font-medium text-slate-100">{focus.chauffeur_name || "Unassigned"}</p>
              <p className="mt-1 text-xs text-slate-500">{focus.vehicle_name}</p>
              <p className="mt-3 text-sm text-slate-300">Trip #{focus.number} · {focus.customer_name}</p>
              <p className="mt-1 text-xs text-slate-400">{stageLabel(focus.stage)}</p>
              <p className="mt-3 text-xs text-slate-400">{focus.pickup?.address} → {focus.dropoff?.address}</p>
              <p className="mt-3 font-mono text-xs text-slate-300">{money(focus.total_charge)}</p>
              <Link to={`/app/manifests/${focus.id}`} data-testid="dispatch-focus-open"
                className="mt-4 inline-block font-mono text-xs text-gold">Open the manifest →</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
